import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { adminAPI } from '../services/api';

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    is_active: true,
    images: ['']
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await adminAPI.getEvents();
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const eventData = {
        ...formData,
        // Clean images: trim whitespace and remove any line breaks from Base64
        images: formData.images
          .map(url => url.replace(/\s+/g, '').trim()) // Remove all whitespace including newlines
          .filter(url => url !== '')
      };

      if (editingEvent) {
        await adminAPI.updateEvent(editingEvent.event_id, eventData);
        alert('Event updated successfully');
      } else {
        await adminAPI.createEvent(eventData);
        alert('Event created successfully');
      }

      resetForm();
      loadEvents();
    } catch (error) {
      alert('Error saving event');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await adminAPI.deleteEvent(eventId);
      loadEvents();
      alert('Event deleted successfully');
    } catch (error) {
      alert('Error deleting event');
    }
  };

  const startEdit = (event) => {
    setEditingEvent(event);
    
    // Format date and time for input fields
    const eventDate = new Date(event.event_date);
    const dateStr = eventDate.toISOString().split('T')[0];
    const timeStr = event.event_time || '';

    // Handle both old format (images string) and new format (imageUrls array)
    let imageArray = [''];
    if (event.imageUrls && Array.isArray(event.imageUrls)) {
      imageArray = event.imageUrls.length > 0 ? event.imageUrls : [''];
    } else if (event.images && typeof event.images === 'string') {
      imageArray = event.images.split(',').filter(img => img.trim());
      if (imageArray.length === 0) imageArray = [''];
    }

    setFormData({
      title: event.title,
      description: event.description,
      event_date: dateStr,
      event_time: timeStr,
      location: event.location || '',
      is_active: event.is_active,
      images: imageArray
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '',
      location: '',
      is_active: true,
      images: ['']
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const handleFileUpload = (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Convert to Base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImages = [...formData.images];
      newImages[index] = reader.result;
      setFormData({ ...formData, images: newImages });
    };
    reader.readAsDataURL(file);
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ''] });
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages.length ? newImages : [''] });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      <Header />
      <main>
        <section className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h1 style={{ color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", margin: '0' }}>
              Event Management
            </h1>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--maroon)',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {showForm ? 'Cancel' : '+ Add New Event'}
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h2 style={{ marginTop: '0', color: 'var(--maroon)' }}>
                {editingEvent ? 'Edit Event' : 'Add New Event'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Event Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Event Date</label>
                      <input
                        type="date"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        required
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Event Time</label>
                      <input
                        type="time"
                        value={formData.event_time}
                        onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Enter event location"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                      <span style={{ fontWeight: '600' }}>Active (visible to users)</span>
                    </label>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Event Images</label>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                      You can upload images, paste Base64 data, or enter image URLs
                    </p>
                    {formData.images.map((img, index) => (
                      <div key={index} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <textarea
                            placeholder="Enter image URL, paste Base64 data, or upload below"
                            value={img}
                            onChange={(e) => handleImageChange(index, e.target.value)}
                            style={{ 
                              flex: 1, 
                              minHeight: '40px',
                              maxHeight: '120px',
                              resize: 'vertical',
                              fontFamily: 'monospace',
                              fontSize: '12px'
                            }}
                          />
                          {formData.images.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeImageField(index)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#dc2626',
                                color: '#fff',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <label style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid var(--maroon)',
                            background: '#fff',
                            color: 'var(--maroon)',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'inline-block'
                          }}>
                            📁 Upload Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(index, e)}
                              style={{ display: 'none' }}
                            />
                          </label>
                          {img && img.trim() && (
                            img.startsWith('http://') || 
                            img.startsWith('https://') || 
                            (img.startsWith('data:image') && img.length > 50)
                          ) && (
                            <img
                              src={img}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addImageField}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--maroon)',
                        background: '#fff',
                        color: 'var(--maroon)',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      + Add Another Image
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={resetForm}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'var(--maroon)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      {editingEvent ? 'Update Event' : 'Create Event'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Events List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              Loading events...
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {events.map((event) => {
                // Handle both old format (images string) and new format (imageUrls array)
                let imageArray = [];
                if (event.imageUrls && Array.isArray(event.imageUrls)) {
                  imageArray = event.imageUrls;
                } else if (event.images && typeof event.images === 'string') {
                  imageArray = event.images.split(',').map(img => img.trim());
                }
                
                const firstImage = imageArray[0] || '';
                return (
                <div key={event.event_id} className="card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    {firstImage && (firstImage.startsWith('http') || firstImage.startsWith('data:image')) && (
                      <img
                        src={firstImage}
                        alt={event.title}
                        style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <h3 style={{ margin: '0 0 8px 0', color: 'var(--maroon)' }}>{event.title}</h3>
                          <p style={{ margin: '0 0 8px 0', color: 'var(--muted)', fontSize: '14px' }}>
                            📅 {formatDate(event.event_date)} {event.event_time && `at ${event.event_time}`}
                          </p>
                          {event.location && (
                            <p style={{ margin: '0 0 8px 0', color: 'var(--muted)', fontSize: '14px' }}>
                              📍 {event.location}
                            </p>
                          )}
                        </div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          background: event.is_active ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
                          color: event.is_active ? '#059669' : '#dc2626',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {event.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <p style={{ margin: '12px 0', lineHeight: '1.6' }}>{event.description}</p>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                        <button
                          onClick={() => startEdit(event)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#3b82f6',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteEvent(event.event_id)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#dc2626',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}

          {events.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              No events found. Click "Add New Event" to get started.
            </div>
          )}
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default AdminEvents;
